import React, { useEffect, useState } from "react";
import { Activity, RefreshCw, CheckCircle2, AlertTriangle, Radio } from "lucide-react";
import api, { friendlyError } from "@/lib/api";
import Layout from "@/components/Layout";
import AiModelsPanel from "@/components/AiModelsPanel";
import { Card, Button, Chip, Skeleton, EmptyState, Toast } from "@/ui";
import { manilaDateTime } from "@/lib/format";

// Platform Owner only. A quick read on whether messaging is healthy across the
// whole platform: is BloomConnect reachable, when it last worked, how many calls
// failed lately, which events we listen to, and each shop's tool check time.
export default function PlatformConnections() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");

  const showToast = (m) => { setToast(m); setTimeout(() => setToast(""), 2800); };

  const load = () => {
    setLoading(true);
    api.get("/conversation/health")
      .then((r) => setData(r.data))
      .catch((e) => setError(friendlyError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const p = data?.provider || {};
  const wh = data?.webhook || {};
  const reachTone = p.reachable === true ? "good" : p.reachable === false ? "bad" : "warn";
  const reachLabel = p.configured
    ? (p.reachable === true ? "reachable" : p.reachable === false ? "not reachable" : "no calls yet")
    : "stand-in (not connected)";

  return (
    <Layout>
      <div className="row-between" style={{ marginBottom: 16 }}>
        <h1 className="t-h1">connections</h1>
        <Button data-testid="refresh-health-btn" onClick={load}><RefreshCw size={16} strokeWidth={1.75} />refresh</Button>
      </div>
      <p className="muted" style={{ marginBottom: 20, maxWidth: 640 }}>
        BloomConnect is the one messaging service for every shop. This is its health at a glance.
      </p>

      <AiModelsPanel onToast={showToast} />

      {error ? <div className="err-text" data-testid="health-error" style={{ marginBottom: 12 }}>{error}</div> : null}

      {loading || !data ? (
        <div className="stack"><Skeleton variant="card" /><Skeleton variant="card" /></div>
      ) : (
        <>
          <div className="grid grid-3" style={{ marginBottom: 16, alignItems: "start" }}>
            <Card data-testid="reachable-card">
              <div className="row" style={{ gap: 8, marginBottom: 10 }}>
                <span className="ph-icon" style={{ margin: 0 }}><Radio size={18} strokeWidth={1.75} /></span>
                <h2 className="t-h3">reachable</h2>
              </div>
              <Chip tone={reachTone} dot data-testid="reachable-chip">{reachLabel}</Chip>
              <div className="row-between" style={{ marginTop: 12 }}>
                <span className="muted t-small">last worked</span>
                <span className="t-small muted-3" data-testid="last-success">{p.lastSuccessAt ? manilaDateTime(p.lastSuccessAt) : "\u2014"}</span>
              </div>
            </Card>

            <Card data-testid="failures-card">
              <div className="row" style={{ gap: 8, marginBottom: 10 }}>
                <span className="ph-icon" style={{ margin: 0 }}><AlertTriangle size={18} strokeWidth={1.75} /></span>
                <h2 className="t-h3">problems (last hour)</h2>
              </div>
              <div className="t-h1 num" data-testid="failures-last-hour">{p.failuresLastHour ?? 0}</div>
              {p.lastError ? <p className="muted t-small" style={{ marginTop: 8, wordBreak: "break-word" }}>{p.lastError}</p> : null}
            </Card>

            <Card data-testid="events-card">
              <div className="row" style={{ gap: 8, marginBottom: 10 }}>
                <span className="ph-icon" style={{ margin: 0 }}><Activity size={18} strokeWidth={1.75} /></span>
                <h2 className="t-h3">listening for</h2>
              </div>
              {(wh.subscribedEvents || []).length === 0 ? (
                <p className="muted t-small" data-testid="no-events">
                  {p.configured ? "not subscribed yet" : "will subscribe once BloomConnect is connected"}
                </p>
              ) : (
                <div className="row" style={{ gap: 6, flexWrap: "wrap" }} data-testid="subscribed-events">
                  {wh.subscribedEvents.map((e) => <Chip key={e} tone="neutral">{e}</Chip>)}
                </div>
              )}
              <div className="row-between" style={{ marginTop: 12 }}>
                <span className="muted t-small">signing secret</span>
                <Chip tone={wh.hasSecret ? "good" : "warn"} dot>{wh.hasSecret ? "stored" : "none yet"}</Chip>
              </div>
              {(wh.missingRequired || []).length ? (
                <p className="err-text t-small" style={{ marginTop: 8 }} data-testid="missing-events">
                  missing needed events: {wh.missingRequired.join(", ")}
                </p>
              ) : null}
            </Card>
          </div>

          <Card>
            <h2 className="t-h2" style={{ marginBottom: 12 }}>shops</h2>
            {(data.shops || []).length === 0 ? (
              <EmptyState icon={<CheckCircle2 size={20} strokeWidth={1.75} />} title="no shops yet" />
            ) : (
              <div className="tbl-wrap" data-testid="shops-health-table">
                <table className="tbl">
                  <thead><tr><th>Shop</th><th>BloomConnect</th><th>Account</th><th>Tools checked</th></tr></thead>
                  <tbody>
                    {data.shops.map((s) => (
                      <tr key={s.shopId} data-testid={`shop-health-${s.shopId}`}>
                        <td>{s.shopName}</td>
                        <td><Chip tone={s.connected ? "good" : "warn"} dot>{s.connected ? "connected" : "not connected"}</Chip></td>
                        <td className="num t-small">{s.subAccountIdMasked || "\u2014"}</td>
                        <td className="muted-3 t-small">{s.toolsVerifiedAt ? manilaDateTime(s.toolsVerifiedAt) : "not yet"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}
      {toast ? <div className="toast-wrap"><Toast tone="good" data-testid="platform-toast">{toast}</Toast></div> : null}
    </Layout>
  );
}
